import { API_ENDPOINTS } from '@/api/endpoints';
import { ProjectShape, useShapeService } from '@/api/services/shapeService';
import { Routes } from '@/CSIRouter';
import { useOrg } from '@/organization/useOrg';
import { fetchSubprojects } from '@/services/subprojectService';
import { SubprojectBuildingDetails } from '@/stores/SubprojectStore';
import { apiFetch } from '@/util/apiClient';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// Fix Leaflet default marker icons (vite asset issue)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const UNASSIGNED_DOT_COLOR = '#9E9E9E';

interface MapThumbnailProps {
  projectID: string;
  fiscal: string | undefined;
}

export function MapThumbnail({ projectID, fiscal }: MapThumbnailProps) {
  const shapeService = useShapeService();
  const { selectedOrg } = useOrg();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  const [shapes, setShapes] = useState<ProjectShape[]>([]);
  const [subprojects, setSubprojects] = useState<SubprojectBuildingDetails[]>(
    [],
  );
  const [shapesLoading, setShapesLoading] = useState(true);
  const [subsLoading, setSubsLoading] = useState(true);

  // Dot-dot-dot menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuSubproject, setMenuSubproject] =
    useState<SubprojectBuildingDetails | null>(null);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] =
    useState<SubprojectBuildingDetails | null>(null);

  // Snackbar feedback
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Load shapes
  useEffect(() => {
    let cancelled = false;
    shapeService
      .list(projectID)
      .then((resp) => {
        if (!cancelled) {
          setShapes(resp.data?.features ?? []);
          setShapesLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setShapesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectID, shapeService]);

  // Load subprojects
  const reloadSubprojects = () => {
    if (!projectID) return;
    fetchSubprojects(projectID)
      .then(setSubprojects)
      .catch(() => {});
  };

  useEffect(() => {
    if (!projectID) return;
    let cancelled = false;
    fetchSubprojects(projectID)
      .then((subs) => {
        if (!cancelled) {
          setSubprojects(subs);
          setSubsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setSubsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectID]);

  // Initialize map once
  useEffect(() => {
    if (!mapContainerRef.current) return;
    const map = L.map(mapContainerRef.current, {
      dragging: false,
      zoomControl: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      keyboard: false,
      attributionControl: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(map);
    map.setView([45, -100], 3);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Render shapes whenever they change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || shapesLoading) return;

    map.eachLayer((layer) => {
      if (!(layer instanceof L.TileLayer)) map.removeLayer(layer);
    });

    if (shapes.length === 0) {
      map.setView([45, -100], 3);
      return;
    }

    const group = L.featureGroup();
    shapes.forEach((shape) => {
      const color = shape.properties.color || '#00008B';
      L.geoJSON(shape, {
        style: { color, fillColor: color, fillOpacity: 0.3, weight: 2 },
      }).addTo(group);
    });
    group.addTo(map);
    try {
      map.fitBounds(group.getBounds());
    } catch {
      map.setView([45, -100], 3);
    }
  }, [shapes, shapesLoading]);

  // Menu handlers
  const handleMenuOpen = (
    e: React.MouseEvent<HTMLElement>,
    sp: SubprojectBuildingDetails,
  ) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuSubproject(sp);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuSubproject(null);
  };

  const handleDuplicate = async () => {
    if (!menuSubproject || !selectedOrg) return;
    handleMenuClose();
    try {
      await apiFetch(
        API_ENDPOINTS.ORGANIZATIONS.SUBPROJECTS.DUPLICATE(
          selectedOrg.id,
          menuSubproject.id,
        ),
        { method: 'POST' },
      );
      reloadSubprojects();
    } catch {
      setSnackbar('Failed to duplicate subproject');
    }
  };

  const handleDeleteClick = () => {
    if (!menuSubproject) return;
    setDeleteTarget(menuSubproject);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || !selectedOrg) return;
    try {
      await apiFetch(
        API_ENDPOINTS.ORGANIZATIONS.SUBPROJECTS.DETAIL(
          selectedOrg.id,
          deleteTarget.id,
        ),
        { method: 'DELETE' },
      );
      setSubprojects((prev) => prev.filter((sp) => sp.id !== deleteTarget.id));
    } catch {
      setSnackbar('Failed to delete subproject');
    } finally {
      setDeleteTarget(null);
    }
  };

  const shapeColorById = new Map(shapes.map((s) => [s.id, s.properties.color]));

  const isMetric = selectedOrg?.measurementSystem === 'metric';
  const assignedShapeIds = new Set(
    subprojects.map((sp) => sp.projectShape).filter(Boolean) as string[],
  );
  const assignedShapes = shapes.filter((s) => assignedShapeIds.has(s.id));
  const totalSqft = assignedShapes.reduce(
    (sum, s) => sum + (s.properties.areaSqft ?? 0),
    0,
  );
  const siteCount = assignedShapeIds.size;
  const areaDisplay =
    siteCount > 0
      ? isMetric
        ? `${(totalSqft / 107639).toFixed(2)} ha`
        : `${(totalSqft / 43560).toFixed(2)} acres`
      : null;

  return (
    <Stack spacing={0} sx={{ height: '100%', minHeight: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant='h6' sx={{ color: 'white', fontWeight: 700 }}>
            Subproject Details
          </Typography>
          {fiscal && (
            <Chip
              label={fiscal}
              size='small'
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'white',
                fontWeight: 600,
              }}
            />
          )}
        </Box>

        <Button
          size='small'
          variant='outlined'
          component={Link}
          to={'../' + Routes.Project.SubprojectCreate.path}
          sx={{
            color: 'white',
            borderColor: 'rgba(255,255,255,0.5)',
            textDecoration: 'none',
          }}
        >
          + New
        </Button>
      </Box>

      {/* Subproject list */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          backgroundColor: 'background.paper',
          borderRadius: 1,
          padding: '3px',
        }}
      >
        {subsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={20} />
          </Box>
        ) : subprojects.length === 0 ? (
          <Typography variant='body2' sx={{ color: 'text.secondary', px: 1 }}>
            No subprojects yet
          </Typography>
        ) : (
          <Stack spacing={0.5} sx={{ px: 1, overflowY: 'auto' }}>
            {subprojects.map((sp) => {
              const dotColor = sp.projectShape
                ? (shapeColorById.get(sp.projectShape) ?? '#00008B')
                : UNASSIGNED_DOT_COLOR;
              return (
                <Stack
                  key={sp.id}
                  direction='row'
                  alignItems='center'
                  spacing={1}
                  sx={{
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    pl: 1.5,
                    pr: 0.5,
                    py: 0.5,
                  }}
                >
                  {/* Color dot */}
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: dotColor,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant='body2'
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sp.name}
                  </Typography>
                  {/* Site Missing chip — shown when no shape is assigned */}
                  {!sp.projectShape && (
                    <Chip
                      icon={
                        <InfoOutlinedIcon
                          sx={{ fontSize: '0.8rem !important' }}
                        />
                      }
                      label='Site Missing'
                      size='small'
                      sx={{
                        bgcolor: 'warning.dark',
                        color: 'warning.contrastText',
                        fontSize: '0.6rem',
                        height: 20,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Button
                    variant='outlined'
                    size='small'
                    component={Link}
                    to={`/project/${projectID}/subproj/${sp.id}`}
                    sx={{
                      textDecoration: 'none',
                      flexShrink: 0,
                      fontSize: '0.6rem',
                      minWidth: 0,
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant='outlined'
                    size='small'
                    component={Link}
                    to={`../site-selection?subprojectId=${sp.id}`}
                    sx={{
                      textDecoration: 'none',
                      flexShrink: 0,
                      fontSize: '0.6rem',
                      minWidth: 0,
                    }}
                  >
                    Edit Site
                  </Button>
                  <IconButton
                    size='small'
                    sx={{ flexShrink: 0 }}
                    onClick={(e) => handleMenuOpen(e, sp)}
                    aria-label='subproject options'
                  >
                    <MoreVertIcon fontSize='small' />
                  </IconButton>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* Dot-dot-dot menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDuplicate}>Duplicate</MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          Delete Subproject
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          {deleteTarget?.projectShape
            ? `YOU ARE REMOVING ${deleteTarget.name} THAT IS ALREADY ASSOCIATED WITH A SITE. ARE YOU SURE YOU WANT TO CONTINUE?`
            : `YOU ARE REMOVING ${deleteTarget?.name}. ARE YOU SURE YOU WANT TO CONTINUE?`}
        </DialogTitle>
        {deleteTarget?.projectShape && (
          <DialogContent>
            <Typography variant='body2'>
              EACH SUBPROJECT NEEDS TO BE TIED TO A SITE
            </Typography>
          </DialogContent>
        )}
        <DialogActions>
          <Button
            onClick={handleDeleteConfirm}
            variant='contained'
            color='info'
          >
            YES
          </Button>
          <Button onClick={() => setDeleteTarget(null)} variant='outlined'>
            CANCEL
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for errors */}
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
      {/* Map */}
      <Box
        sx={{
          position: 'relative',
          height: 280,
          borderRadius: 1,
          overflow: 'hidden',
          flexShrink: 0,
          pt: 4,
        }}
      >
        <div
          ref={mapContainerRef}
          data-testid='map-container'
          style={{ height: '100%', width: '100%' }}
        />
        {shapesLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.4)',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {!shapesLoading && shapes.length === 0 && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
                bgcolor: 'rgba(0,0,0,0.5)',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              No sites defined yet
            </Typography>
          </Box>
        )}
      </Box>

      {/* Site count + area summary */}
      {shapesLoading || subsLoading ? (
        <Stack
          direction='row'
          justifyContent='space-between'
          sx={{ px: 1, pt: 0.75 }}
        >
          <Skeleton variant='text' width={60} />
          <Skeleton variant='text' width={80} />
        </Stack>
      ) : areaDisplay ? (
        <Stack
          direction='row'
          justifyContent='space-between'
          sx={{ px: 1, pt: 0.75 }}
        >
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {siteCount} {siteCount === 1 ? 'site' : 'sites'}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {areaDisplay}
          </Typography>
        </Stack>
      ) : null}
    </Stack>
  );
}
